// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button from "@insite/mobius/Button";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select/Select";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField/TextField";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@keller/App/Components/Notify";
import loadSpecialRequestItemsPriceRange from "@keller/App/Handlers/SpecialRequestItems/LoadPricingRangeSpecialRequestItems";
import submitSpecialRequestItems from "@keller/App/Handlers/SpecialRequestItems/SendSpecialRequestItems";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { xIsAuthenticated } from "@keller/App/Store/Selectors/GeneralSelectors";
import {
    initialSpecialRequestItemsFormState,
    SpecialRequestItemsFormState,
} from "@keller/App/Widgets/SpecialRequestItems/SpecialRequestItems.form.module";
import { AccordionSectionPresentationProps } from "@keller/MobiusOverrides/AccordionSection";
import { ErrorApiDefaultMessage } from "@keller/XDL/constants";
import { color, xMask } from "@keller/XDL/Utilities";
import { CreateForm } from "@keller/XDL/Utilities/form";
import cloneDeep from "lodash/cloneDeep";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    priceRange: state.xSpecialRequestItems.priceRange,
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    loadSpecialRequestItemsPriceRange,
    submitSpecialRequestItems,
};

type Props = OwnProps & WidgetModule & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface SpecialRequestItemsStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    formWrapper?: InjectableCss;
    textField?: TextFieldPresentationProps;
    textArea?: TextAreaPresentationProps;
    selectField?: SelectPresentationProps;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    notifyMessage?: TypographyProps;
}

const specialRequestItemsStyles: SpecialRequestItemsStyles = {
    formWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            > div,
            > .notify {
                margin-bottom: 15px;
            }
        `,
    },
    textArea: {
        mergeCss: true,
        css: css`
            max-height: 160px;
            resize: vertical;
        `,
    },
    accordion: {
        css: css`
            box-shadow: 0 2px 0 0 ${color("common.ui5")};
        `,
    },
    accordionSection: {
        mergeCss: true,
        headerProps: {
            css: css`
                &&& button {
                    padding: 14px 17px;
                    background: ${color("common.ui12")};
                    border-bottom: 1px solid ${color("common.border")};
                }
            `,
        },
        panelProps: {
            css: css`
                background: ${color("common.white")};
                padding: 14px 17px;
            `,
        },
        titleTypographyProps: {
            size: 14,
            weight: "bold",
        },
    },
    notifyMessage: {
        css: css`
            &&& {
                min-width: 100%;
            }
        `,
    },
};

const styles = specialRequestItemsStyles;

const SpecialRequestItems: FC<Props> = ({
    fields: { title },
    priceRange,
    isAuthenticated,
    submitSpecialRequestItems,
    loadSpecialRequestItemsPriceRange,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const toaster = useToaster();

    const [specialRequestItemsFormState, setSpecialRequestItemsFormState] = useState<SpecialRequestItemsFormState>(
        cloneDeep(initialSpecialRequestItemsFormState),
    );
    const specialRequestItemsForm = new CreateForm<SpecialRequestItemsFormState>(
        specialRequestItemsFormState,
        setSpecialRequestItemsFormState,
    );

    useEffect(() => {
        if (!priceRange || priceRange.length === 0) {
            loadSpecialRequestItemsPriceRange({});
        }
    }, []);

    const onSubmitHandler = () => {
        const { isSubmitted } = specialRequestItemsForm.state;

        if (!isSubmitted) {
            specialRequestItemsForm.setSubmitted();
        }

        if (isSubmitting) {
            return;
        }

        if (!specialRequestItemsForm.validate()) {
            return;
        }

        setIsSubmitting(true);
        setErrorMessage("");

        const formState: { [key: string]: string } = {};
        Object.keys(specialRequestItemsForm.state.fields).forEach(key => {
            formState[key] = (specialRequestItemsForm.state.fields as any)?.[key].value;
        });

        submitSpecialRequestItems({
            ...formState,
            onSuccess: () => {
                setIsSubmitting(false);
                setErrorMessage("");
                toaster.addToast({
                    body: siteMessage("Keller_SpecialRequestItemsSentSuccessfully"),
                    messageType: "success",
                });
                resetFields();
            },
            onError: errorMessage => {
                setIsSubmitting(false);
                const failedMessage = errorMessage || translate(ErrorApiDefaultMessage);
                logger.error(failedMessage);
                setErrorMessage(failedMessage);
            },
        });
    };

    const resetFields = () => {
        setSpecialRequestItemsFormState(cloneDeep(initialSpecialRequestItemsFormState));
    };

    if (!isAuthenticated) {
        return null;
    }

    const { fields, isSubmitted } = specialRequestItemsForm.state;
    const isFormValid = specialRequestItemsForm.validate();

    return (
        <Accordion headingLevel={2} {...styles.accordion}>
            <ManagedAccordionSection title={title} initialExpanded={false} {...styles.accordionSection}>
                <StyledWrapper {...styles.formWrapper}>
                    <Select
                        {...styles.selectField}
                        value={fields.priceRange.value}
                        onChange={e => specialRequestItemsForm?.onFieldChange(fields.priceRange, e.currentTarget.value)}
                        error={isSubmitted && fields.priceRange.error}
                        {...fields.priceRange.attrs}
                    >
                        <option value="">{translate("Price Range")}</option>
                        {priceRange?.map(price => (
                            <option key={price.value} value={price.value}>
                                {price.displayName}
                            </option>
                        ))}
                    </Select>
                    <TextField
                        value={fields.qtyNeeded.value}
                        onChange={e =>
                            specialRequestItemsForm?.onFieldChange(fields.qtyNeeded, xMask.qty(e.currentTarget.value))
                        }
                        error={isSubmitted && fields.qtyNeeded.error}
                        {...fields.qtyNeeded.attrs}
                        {...styles.textField}
                    />
                    <TextField
                        value={fields.upcNumber.value}
                        onChange={e => specialRequestItemsForm?.onFieldChange(fields.upcNumber, e.currentTarget.value)}
                        error={isSubmitted && fields.upcNumber.error}
                        {...fields.upcNumber.attrs}
                        {...styles.textField}
                    />
                    <TextField
                        value={fields.manufacturerName.value}
                        onChange={e =>
                            specialRequestItemsForm?.onFieldChange(fields.manufacturerName, e.currentTarget.value)
                        }
                        error={isSubmitted && fields.manufacturerName.error}
                        {...fields.manufacturerName.attrs}
                        {...styles.textField}
                    />
                    <TextField
                        value={fields.mfgNumber.value}
                        onChange={e => specialRequestItemsForm?.onFieldChange(fields.mfgNumber, e.currentTarget.value)}
                        error={isSubmitted && fields.mfgNumber.error}
                        {...fields.mfgNumber.attrs}
                        {...styles.textField}
                    />
                    <TextField
                        value={fields.manufacturerProductName.value}
                        onChange={e =>
                            specialRequestItemsForm?.onFieldChange(
                                fields.manufacturerProductName,
                                e.currentTarget.value,
                            )
                        }
                        error={isSubmitted && fields.manufacturerProductName.error}
                        {...fields.manufacturerProductName.attrs}
                        {...styles.textField}
                    />
                    <TextArea
                        value={fields.description.value}
                        onChange={e =>
                            specialRequestItemsForm?.onFieldChange(fields.description, e.currentTarget.value)
                        }
                        error={isSubmitted && fields.description.error}
                        {...fields.description.attrs}
                        {...styles.textArea}
                    />
                    <TextArea
                        value={fields.comments.value}
                        onChange={e => specialRequestItemsForm?.onFieldChange(fields.comments, e.currentTarget.value)}
                        error={isSubmitted && fields.comments.error}
                        {...fields.comments.attrs}
                        {...styles.textArea}
                    />
                    {errorMessage && (
                        <Notify {...styles.notifyMessage} type="danger">
                            {errorMessage}
                        </Notify>
                    )}
                    <Button
                        onClick={onSubmitHandler}
                        disabled={isSubmitting || (!isFormValid && specialRequestItemsForm.state.isSubmitted)}
                    >
                        {isSubmitting ? <LoadingSpinner size={12} /> : translate("Send")}
                    </Button>
                </StyledWrapper>
            </ManagedAccordionSection>
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SpecialRequestItems),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Add Non-Stock Products",
            },
        ],
    },
};

export default widgetModule;
